/**
 * PF Revamp — shared configuration flow resolver.
 *
 * Single source of truth mapping a `(productType, kind?)` tuple — plus optional
 * per-product flags — to an array of `ConfigurationFlowId`. Used by:
 *   - PF revamp cart→configurator handoff (`CartSidebar.handleProceed`,
 *     `ProductConfiguration.page.tsx`) as a **fallback** when the BE-fed
 *     `shell.configurationFlows` is empty. Gated behind the Unleash flag
 *     `ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER` so production stays
 *     BE-driven while dogfood (`dev.bkbn.com`) can resolve flows FE-side
 *     until BE populates `ConfigurationFlows__c` (multipicklist) on Product2.
 *
 * Multi-flow contract (Phase A.5):
 *   The resolver returns an array because a product could require more than one
 *   configurator; today every product resolves to exactly one, and
 *   non-configurable products yield `[]`. A configurator with several pages (the
 *   energy certificate, the authorities document) is still ONE flow — the pages
 *   live inside the module.
 *
 * Resolution order (mirrors VARIANT_SECTION_LABEL_BY_PT_KIND in the
 * translator — same `${productType}|${kind}` key form):
 *   1. CONFIG_FLOW_BY_PT_KIND        keyed `${productType}|${kind}` → primary flow
 *   2. CONFIG_FLOW_BY_PRODUCT_TYPE   keyed productType              → primary flow
 *   3. No match → empty array.
 */

import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'

/** (productType|kind) → primary configurationFlowId override.
 *  Use this when a kind under a productType maps to a different flow than
 *  the productType-level default. */
export const CONFIG_FLOW_BY_PT_KIND: Readonly<Partial<Record<string, ConfigurationFlowId>>> = Object.freeze({
  // Floor-plan family — productType FLOOR_PLAN spans 3 distinct configurators.
  'FLOOR_PLAN|MEASUREMENT_ON_SITE': 'measurement',
  'FLOOR_PLAN|FLOOR_PLAN_CERTIFICATION': 'floor_plan_certification',
  // Explicit mapping (matches productType-level default — listed for clarity).
  'FLOOR_PLAN|MOBILE_APPLICATION_FLOOR_PLAN': 'floor_plan_config',
  // Virtual staging under productType PHOTO (F12 fix).
  // The legacy BE snapshot captures RESIDENTIAL_STAGING and COMMERCIAL_STAGING
  // as kinds under productType=PHOTO. Both require the same staging configurator
  // as STAGING_360 products. Without this entry, resolveConfigurationFlows falls
  // through to the productType-level table where 'PHOTO' has no entry, yielding
  // an empty array — causing the configuration step to be skipped entirely for
  // these products.
  'PHOTO|RESIDENTIAL_STAGING': 'staging',
  'PHOTO|COMMERCIAL_STAGING': 'staging',
})

/** productType → primary configurationFlowId fallback. */
export const CONFIG_FLOW_BY_PRODUCT_TYPE: Readonly<Partial<Record<string, ConfigurationFlowId>>> = Object.freeze({
  STAGING_360: 'staging',
  FLOOR_PLAN: 'floor_plan_config',
  ENERGY_CERTIFICATE_OF_NEED: 'energy_certificate_of_need',
  CONSUMPTION_CERTIFICATE: 'consumption_certificate',
  LAND_REGISTRATION: 'power_of_attorney',
  MUNICIPAL: 'power_of_attorney',
  LAW_INQUIRIES: 'power_of_attorney',
  PROTECTION: 'power_of_attorney',
  CONSTRUCTION_FILE: 'power_of_attorney',
  DOCUMENTATION: 'power_of_attorney',
})

/**
 * Resolve the array of `ConfigurationFlowId`s for a given `(productType, kind?)` tuple.
 *
 * @param productType Salesforce Product2 productType key (e.g. 'FLOOR_PLAN').
 * @param kind        Salesforce productKind discriminator (optional).
 * @returns Ordered list of flows; empty when no configurator is mapped.
 */
export function resolveConfigurationFlows(
  productType: string,
  kind?: string
): ConfigurationFlowId[] {
  const primary = (kind ? CONFIG_FLOW_BY_PT_KIND[`${productType}|${kind}`] : undefined)
    ?? CONFIG_FLOW_BY_PRODUCT_TYPE[productType]

  if (!primary) return []

  return [primary]
}
